// src/modules/Rules/hooks/useCreateRule.ts
// Hook personalizado que gestiona el estado y lógica para crear o editar una regla de envío, incluyendo validaciones y comunicación con la API.
import { useToast } from '@shared/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import type { ActionType } from '../components/display/RuleActionsSection';
import type { Condition } from '../components/display/RuleConditionsSection';
import type { ConfirmCreateRuleRef } from '../components/modals/ConfirmCreateRule';
import { RulesModel } from '../models/Rules';
import { useShippingRuleForm } from './useShippingRuleForm';

export interface UseCreateRuleResult {
  isEditMode: boolean;
  loadingRule: boolean;
  loadingCondsMeta: boolean;

  ruleName: string;
  setRuleName: (v: string) => void;

  matchAll: boolean;
  setMatchAll: (v: boolean) => void;

  conditions: Condition[];
  onAddCondition: () => void;
  onUpdateCondition: (id: number, key: keyof Condition, val: string) => void;
  onRemoveCondition: (id: number) => void;

  conditionOptions: { id: string; name: string }[];
  loadOperators: (field: string) => Promise<{ id: string; name: string }[]>;
  loadValues: (field: string) => Promise<{
    options: { id: string; name: string }[];
    valueType: 'select' | 'text' | 'numeric';
  }>;
  loadCourierProducts: (integrationId: string) => Promise<{ id: string; name: string }[]>;
  findCourierByServiceId: (serviceId: string) => Promise<string | null>;

  actionType: ActionType;
  onActionTypeChange: (type: ActionType) => void;
  courierIntegrations: { id: number; name: string }[];
  courierProducts: { id: number; name: string }[];
  loadingProducts: boolean;
  selectedCourierIntegrationId: string;
  selectedCourierProductId: string;
  onCourierChange: (id: string) => void;
  onProductChange: (id: string) => void;

  // Estado para courier inactivo
  isSelectedCourierInactive: boolean;
  inactiveCourierName: string;

  onCancel: () => void;
  onSubmit: () => void;

  confirmRef: React.RefObject<ConfirmCreateRuleRef | null>;
}

export interface UseCreateRuleOptions {
  /** team_id del tenant_external (cliente hijo). Obligatorio para crear reglas desde /clients/:clientId/rules */
  teamId?: number;
  /** Ruta a la que navegar tras crear/editar con éxito. Por defecto: /configuration/rules */
  successNavigateTo?: string;
  /** Ruta a la que navegar al cancelar. Por defecto: /configuration/rules */
  cancelNavigateTo?: string;
  /**
   * ID de regla a editar sin `:id` en la URL (p. ej. panel lateral en /clients/.../rules).
   * Si se define, tiene prioridad sobre el parámetro de ruta `id`.
   */
  editingRuleId?: number;
  /**
   * Al guardar o cancelar se llama a este callback en lugar de `navigate` (p. ej. cierre de panel).
   */
  onFlowEnd?: () => void;
  /**
   * Regla ya cargada (p. ej. listado). Si coincide con el id en edición, no se hace GET /shipping-rules/:id.
   */
  initialRule?: RulesModel;
  /**
   * Si se define, no se persiste en /shipping-rules: el padre guarda el borrador (p. ej. presets).
   * El payload es el mismo que enviaría la API (puede incluir `team_id` si hay `teamId` en opciones).
   */
  onLocalPersist?: (
    payload: Record<string, unknown>,
    context: { isEditMode: boolean; activeRuleId?: number },
  ) => Promise<void>;
}

export function useCreateRule(options: UseCreateRuleOptions = {}): UseCreateRuleResult {
  const {
    teamId,
    successNavigateTo = '/configuration/rules',
    cancelNavigateTo = '/configuration/rules',
    editingRuleId: editingRuleIdOption,
    onFlowEnd,
    initialRule: initialRuleOption,
    onLocalPersist,
  } = options;
  const { t } = useTranslation('rulesModule');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id?: string }>();
  const activeRuleId = useMemo(() => {
    if (editingRuleIdOption != null) return editingRuleIdOption;
    if (routeId) return Number(routeId);
    return undefined;
  }, [editingRuleIdOption, routeId]);
  const isEditMode = activeRuleId != null && !Number.isNaN(activeRuleId);
  const confirmRef = useRef<ConfirmCreateRuleRef>(null);
  const queryClient = useQueryClient();

  const {
    conditionOptions,
    loadOperators,
    loadValues,
    courierIntegrations,
    allCourierIntegrations,
    loadCourierProducts,
    findCourierByServiceId,
    getCourierIntegrationById,
    getRuleById,
    createRule,
    updateRule,
  } = useShippingRuleForm({ teamId });

  const [loadingRule, setLoadingRule] = useState(isEditMode);
  const [loadingCondsMeta, setLoadingCondsMeta] = useState(isEditMode);
  const [ruleName, setRuleName] = useState('');
  const [matchAll, setMatchAll] = useState(true);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [actionType, setActionType] = useState<ActionType>('courier_integration_id');
  const [selectedCourierIntegrationId, setSelectedCourierIntegrationId] = useState('');
  const [selectedCourierProductId, setSelectedCourierProductId] = useState('');
  const [courierProducts, setCourierProducts] = useState<{ id: number; name: string }[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const editHydrationDoneForId = useRef<number | null>(null);

  useEffect(() => {
    if (!isEditMode) {
      editHydrationDoneForId.current = null;
      return;
    }
    if (activeRuleId == null || allCourierIntegrations.length === 0) return;
    if (editHydrationDoneForId.current === activeRuleId) return;

    setLoadingRule(true);
    setLoadingCondsMeta(true);
    const start = Date.now();

    (async () => {
      try {
        // 1) regla: datos locales si coinciden el id, si no GET
        const rule =
          initialRuleOption != null && initialRuleOption.id === activeRuleId
            ? initialRuleOption
            : await getRuleById(activeRuleId);
        setRuleName(rule.name);
        setMatchAll(rule.rules.match_type === 'all');

        // 2) montar condiciones
        const initialConds = rule.rules.conditions.map((c, i) => ({
          id: Date.now() + i,
          field: c.field,
          operator: c.operator,
          value: c.value,
        }));
        setConditions(initialConds);

        // 3) precargar operadores y valores (optimizado para evitar llamadas duplicadas)
        const uniqueFields = [...new Set(initialConds.map((c) => c.field))];
        await Promise.all(uniqueFields.map((field) => Promise.all([loadOperators(field), loadValues(field)])));
        setLoadingCondsMeta(false);

        // 4) detectar tipo de acción y cargar datos si es courier
        const courierAction = rule.actions.find((a) => a.action === 'courier_integration_id');
        const noEnviarAction = rule.actions.find((a) => a.action === 'no_enviar');

        if (noEnviarAction) {
          setActionType('no_enviar');
        } else if (courierAction) {
          setActionType('courier_integration_id');
          const cid = String(courierAction.value);
          setSelectedCourierIntegrationId(cid);
          setSelectedCourierProductId(String((courierAction as any).courier_service_id ?? ''));

          setLoadingProducts(true);
          const prods = await loadCourierProducts(cid);
          setCourierProducts(prods);
          setLoadingProducts(false);
        }
        editHydrationDoneForId.current = activeRuleId;
      } catch (e) {
        console.error(e);
      } finally {
        // 5) retraso mínimo para evitar parpadeos
        const elapsed = Date.now() - start;
        const MIN_DELAY = 500;
        setTimeout(() => setLoadingRule(false), Math.max(0, MIN_DELAY - elapsed));
      }
    })();
  }, [
    isEditMode,
    activeRuleId,
    allCourierIntegrations,
    initialRuleOption,
    getRuleById,
    loadOperators,
    loadValues,
    loadCourierProducts,
    getCourierIntegrationById,
  ]);

  const saveRule = useCallback(async () => {
    try {
      if (!ruleName.trim()) throw new Error(t('create.errors.name_required'));
      if (actionType === 'courier_integration_id') {
        if (!selectedCourierIntegrationId) throw new Error(t('create.errors.courier_required'));
        if (!selectedCourierProductId) throw new Error(t('create.errors.product_required'));
      }

      const validConds = conditions
        .filter((c) => c.field && c.operator && c.value !== '')
        .map((c) => ({ field: c.field, operator: c.operator, value: c.value }));

      const actions =
        actionType === 'no_enviar'
          ? [{ action: 'no_enviar', value: '' }]
          : [
              {
                action: 'courier_integration_id',
                value: selectedCourierIntegrationId,
                courier_service_id: Number(selectedCourierProductId),
              },
            ];

      const payload: Record<string, unknown> = {
        name: ruleName.trim(),
        is_active: true,
        rules: { match_type: matchAll ? 'all' : 'any', conditions: validConds },
        actions,
      };

      // Incluir team_id solo para reglas de un cliente concreto (/clients/:id/rules)
      if (teamId != null && Number(teamId) > 0) {
        payload.team_id = teamId;
      }

      if (onLocalPersist) {
        await onLocalPersist(payload, {
          isEditMode,
          activeRuleId: activeRuleId ?? undefined,
        });
        toast({
          title: isEditMode ? t('create.draft_updated') : t('create.draft_added'),
          description: isEditMode ? t('create.draft_updated_description') : t('create.draft_added_description'),
        });
      } else if (isEditMode && activeRuleId != null) {
        await updateRule(activeRuleId, payload);
        toast({
          title: t('create.updated'),
          description: t('create.updated_description'),
        });
      } else {
        await createRule(payload);
        toast({
          title: t('create.success'),
          description: t('create.success_description'),
        });
      }

      if (!onLocalPersist) {
        queryClient.invalidateQueries({ queryKey: ['rules'] });
      }
      if (onFlowEnd) {
        onFlowEnd();
      } else {
        navigate(successNavigateTo);
      }
    } catch (err: any) {
      toast({
        title: t('create.error'),
        description: err.message || t('create.error_description'),
        variant: 'destructive',
      });
    }
  }, [
    ruleName,
    matchAll,
    conditions,
    actionType,
    selectedCourierIntegrationId,
    selectedCourierProductId,
    isEditMode,
    activeRuleId,
    teamId,
    successNavigateTo,
    onFlowEnd,
    t,
    toast,
    navigate,
    createRule,
    updateRule,
    queryClient,
    onLocalPersist,
  ]);

  const onSubmit = () => {
    try {
      if (!ruleName.trim()) throw new Error(t('create.errors.name_required'));
      if (actionType === 'courier_integration_id') {
        if (!selectedCourierIntegrationId) throw new Error(t('create.errors.courier_required'));
        if (!selectedCourierProductId) throw new Error(t('create.errors.product_required'));
      }
    } catch (err: any) {
      toast({
        title: t('create.error'),
        description: err.message,
        variant: 'destructive',
      });
      return;
    }
    saveRule();
  };

  const onAddCondition = useCallback(
    () => setConditions((prev) => [...prev, { id: Date.now(), field: '', operator: '', value: '' }]),
    [],
  );

  const onUpdateCondition = useCallback(
    (cid: number, key: keyof Condition, val: string) =>
      setConditions((prev) => prev.map((c) => (c.id === cid ? { ...c, [key]: val } : c))),
    [],
  );

  const onRemoveCondition = useCallback((cid: number) => setConditions((prev) => prev.filter((c) => c.id !== cid)), []);

  const onCourierChange = useCallback(
    async (integrationId: string) => {
      setSelectedCourierIntegrationId(integrationId);
      setSelectedCourierProductId('');

      setLoadingProducts(true);
      try {
        console.log('🔍 useCreateRule - Cargando productos para integrationId:', integrationId);
        const prods = await loadCourierProducts(integrationId);
        console.log('🔍 useCreateRule - Productos recibidos:', prods);
        setCourierProducts(prods);
      } catch (error) {
        console.log('🔍 useCreateRule - Error cargando productos:', error);
        setCourierProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    },
    [loadCourierProducts],
  );

  const onProductChange = useCallback((id: string) => setSelectedCourierProductId(id), []);
  const onActionTypeChange = useCallback((type: ActionType) => {
    setActionType(type);
    if (type === 'no_enviar') {
      setSelectedCourierIntegrationId('');
      setSelectedCourierProductId('');
      setCourierProducts([]);
    }
  }, []);
  const onCancel = useCallback(() => {
    if (onFlowEnd) onFlowEnd();
    else navigate(cancelNavigateTo);
  }, [navigate, cancelNavigateTo, onFlowEnd]);

  // Transformar courierIntegrations de IntegrationOption (id: string) a formato esperado (id: number)
  // para que coincida con la interfaz esperada por los componentes
  const transformedCourierIntegrations = courierIntegrations.map((integration) => ({
    id: Number(integration.id),
    name: integration.name,
  }));

  return {
    isEditMode,
    loadingRule,
    loadingCondsMeta,

    ruleName,
    setRuleName,
    matchAll,
    setMatchAll,
    conditions,
    onAddCondition,
    onUpdateCondition,
    onRemoveCondition,

    conditionOptions,
    loadOperators,
    loadValues,
    loadCourierProducts,
    findCourierByServiceId,

    actionType,
    onActionTypeChange,
    courierIntegrations: transformedCourierIntegrations,
    courierProducts,
    loadingProducts,
    selectedCourierIntegrationId,
    onCourierChange,
    selectedCourierProductId,
    onProductChange,

    onCancel,
    onSubmit,
    confirmRef,

    // Estado para courier inactivo
    isSelectedCourierInactive: false,
    inactiveCourierName: '',
  };
}
